import { Controller } from "@hotwired/stimulus"

// Loads onboarding_5's profile receipt and routine preview when their step is shown.
//
// Every onboarding step is rendered with the page, so a frame with a src (lazy
// or not) can be fetched before the user has answered the preference questions
// this screen reports on. The frame therefore ships without a src, and this
// controller sets it on entry to the step, re-fetching on every entry so a
// back-nav that changes an answer produces a corrected receipt.
//
// The step's answers are PUT asynchronously as the user leaves each step, so a
// short settle delay gives the last in-flight write time to land before we
// count against it.
export default class extends Controller {
    static targets = ["frame"]
    static values = {
        src: String,
        stepKey: String,
        settleDelay: { type: Number, default: 300 }
    }

    connect() {
        this.onStepShown = this.handleStepShown.bind(this)
        window.addEventListener("onboarding:step-shown", this.onStepShown)

        // Covers a direct render of the step (no transition to observe) and any
        // client that shows the step before this controller connects.
        if (this.visible) this.load()
    }

    disconnect() {
        window.removeEventListener("onboarding:step-shown", this.onStepShown)
        if (this.timeout) clearTimeout(this.timeout)
    }

    handleStepShown(event) {
        if (event.detail?.stepKey !== this.stepKeyValue) return

        this.load()
    }

    load() {
        if (!this.hasFrameTarget || !this.srcValue) return

        const frame = this.frameTarget.querySelector("turbo-frame")
        if (!frame) return

        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
            // A distinct src each time so Turbo re-fetches rather than treating
            // an unchanged src as already loaded.
            frame.src = `${this.srcValue}?t=${Date.now()}`
        }, this.settleDelayValue)
    }

    get visible() {
        const step = this.element.closest('[data-onboarding-target="step"]')

        return step ? !step.classList.contains("hidden") : true
    }
}
